// frontend/src/utils/settlementUtils.js
import { formatMoney } from './money';

export const getStatusConfig = (status) => {
  switch(status) {
    case 'Won':
      return {
        color: 'text-green-500',
        bgColor: 'bg-green-500/10',
        borderColor: 'border-green-500/20',
        icon: '✅'
      };
    case 'Lost':
      return {
        color: 'text-red-500',
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500/20',
        icon: '❌'
      };
    case 'Pending':
      return {
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-500/10',
        borderColor: 'border-yellow-500/20',
        icon: '⏳'
      };
    case 'Partially_Won':
      return {
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
        borderColor: 'border-blue-500/20',
        icon: '🔄'
      };
    default:
      return {
        color: 'text-gray-500',
        bgColor: 'bg-gray-500/10',
        borderColor: 'border-gray-500/20',
        icon: '❓'
      };
  }
};

// Buckets a bet into one of the two My Bets tabs:
//   open    -> Pending, Under_Review        (not yet resolved)
//   settled -> everything else              (Won, Partially_Won, Lost,
//                                            Voided, Cancelled, Settled)
// The per-bet status badge still shows the specific outcome.
export const betCategory = (bet) => {
  const key = (bet?.status || '').toLowerCase().replace(/[\s_-]/g, '');
  switch (key) {
    case 'pending':
    case 'underreview':
      return 'open';
    default:
      return 'settled';
  }
};

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return formatMoney(0);
  return formatMoney(amount);
};

// Long feed names (e.g. "Ederson Jose dos Santos Lourenco da Silva") overflow
// cards/modals. Shorten to first + last for DISPLAY only - the full name stays on
// the pick for settlement matching.
export const shortenPlayerName = (name) => {
  const n = String(name || '').trim();
  if (n.length <= 18) return n;
  const parts = n.split(/\s+/);
  if (parts.length <= 2) return n;
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(decimals)}%`;
};

const _isGoalscorerStat = (stat) => /goalscorer/i.test(stat || '');
const _formatAsOf = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return null; }
};

// Live progress for the My Bets bar. Returns null unless the pick's game is
// in-play (server `liveState === 'live'`, or fallback pending + a live value).
// Otherwise: { ratio: 0..1, currentLabel, targetLabel, asOf, tone }.
export const livePickProgress = (pick) => {
  if (pick == null) return null;
  const state = String(pick.liveState || '').toLowerCase();
  const status = String(pick.status || '').toLowerCase();
  const isLive = state ? state === 'live' : (status === 'pending' && pick.liveValue != null);
  if (!isLive || pick.liveValue == null) return null; // need a real value to show progress

  const current = Number(pick.liveValue);
  const line = Number(pick.line);
  const isUnder = String(pick.pickType || '').toLowerCase() === 'under';
  const asOf = _formatAsOf(pick.liveUpdatedAt);

  if (_isGoalscorerStat(pick.statType)) {
    const scored = current > 0.5;
    return { ratio: scored ? 1 : 0, currentLabel: scored ? 'Scored' : 'Not yet', targetLabel: null, asOf, tone: scored ? 'good' : 'warn' };
  }
  if (isUnder) {
    const ratio = line > 0 ? Math.min(1, current / line) : 0;
    return { ratio, currentLabel: `${current}`, targetLabel: `under ${line}`, asOf, tone: current >= line ? 'bad' : 'good' };
  }
  const need = Math.max(1, Math.ceil(line));
  const ratio = Math.min(1, current / need);
  return { ratio, currentLabel: `${current}`, targetLabel: `needs ${need}`, asOf, tone: current > line ? 'good' : 'warn' };
};

export const calculateTimeAgo = (date) => {
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  
  return past.toLocaleDateString();
};

export const calculateSettlementProgress = (bet) => {
  if (!bet.picks || bet.picks.length === 0) return 0;
  
  const resolvedPicks = bet.picks.filter(pick => 
    pick.status !== 'Pending' && pick.status !== 'Under_Review'
  ).length;
  
  return Math.round((resolvedPicks / bet.picks.length) * 100);
};

export const getBetTypeLabel = (type) => {
  switch(type) {
    case 'PerfectPick': return 'Beast Mode';
    case 'SmartPlay': return 'Safety Play';
    default: return type;
  }
};

export const getBetTypeColor = (type) => {
  switch(type) {
    case 'PerfectPick': return 'bg-red-500/20 text-red-400';
    case 'SmartPlay': return 'bg-green-500/20 text-green-400';
    default: return 'bg-gray-500/20 text-gray-400';
  }
};

export const validateBetData = (bet) => {
  const errors = [];
  
  if (!bet.id) errors.push('Bet ID is required');
  if (!bet.userId) errors.push('User ID is required');
  if (!bet.amount || bet.amount <= 0) errors.push('Valid bet amount is required');
  if (!bet.type) errors.push('Bet type is required');
  if (!bet.picks || bet.picks.length === 0) errors.push('At least one pick is required');
  
  return {
    isValid: errors.length === 0,
    errors
  };
};


export const getHealthStatus = (value, thresholds) => {
  if (value >= thresholds.healthy) return 'healthy';
  if (value >= thresholds.warning) return 'warning';
  return 'error';
};

export const formatMetricValue = (value, type = 'number') => {
  switch(type) {
    case 'currency':
      return formatCurrency(value);
    case 'percentage':
      return formatPercentage(value);
    case 'number':
      return value?.toLocaleString() || '0';
    default:
      return value?.toString() || '0';
  }
};

// frontend/src/utils/apiUtils.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export const apiRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('playerbet_token') || localStorage.getItem('playerbet_admin_token');
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    },
    ...options
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      // The API returns the specific reasons in `errors`; showing only `message`
      // turned every rejection into an undiagnosable "Bet validation failed".
      const detail = Array.isArray(error?.errors) && error.errors.length
        ? error.errors.join('\n')
        : null;
      throw new Error(detail || error.message || `HTTP ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API Request failed for ${endpoint}:`, error);
    throw error;
  }
};

export const adminApiRequest = async (endpoint, options = {}) => {
  const adminToken = localStorage.getItem('playerbet_admin_token');
  
  if (!adminToken) {
    throw new Error('Admin authentication required');
  }

  return apiRequest(endpoint, {
    ...options,
    headers: {
      'Authorization': `Bearer ${adminToken}`,
      ...options.headers
    }
  });
};

// Specific API calls
export const fetchUserBets = async (userId, filters = {}) => {
  const queryParams = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value);
    }
  });

  const queryString = queryParams.toString();
  const endpoint = `/bets/user/${userId}${queryString ? `?${queryString}` : ''}`;
  
  return apiRequest(endpoint);
};

export const fetchBetDetails = async (betId) => {
  return apiRequest(`/bets/${betId}`);
};

export const calculatePayout = async (picks, type, amount) => {
  return apiRequest('/bets/calculate-payout', {
    method: 'POST',
    body: JSON.stringify({ picks, type, amount })
  });
};

export const placeBet = async (betData) => {
  return apiRequest('/bets', {
    method: 'POST',
    body: JSON.stringify(betData)
  });
};

export const fetchAdminDashboard = async () => {
  // Admin monitoring routes live at the server root, not under /api
  const token = localStorage.getItem('playerbet_admin_token') || localStorage.getItem('playerbet_token');
  const response = await fetch(`${API_BASE_URL.replace(/\/api$/, '')}/admin/monitoring/dashboard`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
};

export const fetchSystemHealth = async () => {
  try {
    const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health/detailed`);
    return await response.json();
  } catch (error) {
    console.error('Health check failed:', error);
    return { status: 'Unhealthy', checks: [] };
  }
};

// Export all utilities
// export {
//   formatPercentage,
//   calculateTimeAgo,
//   calculateSettlementProgress,
//   getBetTypeLabel,
//   getBetTypeColor,
//   validateBetData,
//   getHealthStatus,
//   formatMetricValue,
//   apiRequest,
//   adminApiRequest,
//   calculatePayout,
//   placeBet
// };